import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export function useNotifications() {
    return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // Subscribe to notifications for the current user
    useEffect(() => {
        if (!user?.uid || !db) {
            setNotifications([]);
            setUnreadCount(0);
            setLoading(false);
            return;
        }

        setLoading(true);

        // Create query for user's notifications
        const notificationsRef = collection(db, 'notifications');
        const q = query(
            notificationsRef,
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        // Subscribe to real-time updates
        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const notificationsList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
                }));

                setNotifications(notificationsList);
                setUnreadCount(notificationsList.filter(n => !n.read).length);
                setLoading(false);
            },
            (error) => {
                console.error('Error subscribing to notifications:', error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user?.uid]);

    // Mark a single notification as read
    const markAsRead = useCallback(async (notificationId) => {
        if (!db) return;

        try {
            const notificationRef = doc(db, 'notifications', notificationId);
            await updateDoc(notificationRef, { read: true });

            // Update local state optimistically
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }, []);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        if (!db || !user?.uid) return;

        try {
            const unreadNotifications = notifications.filter(n => !n.read);
            if (unreadNotifications.length === 0) return;

            const batch = writeBatch(db);
            unreadNotifications.forEach(notification => {
                const notificationRef = doc(db, 'notifications', notification.id);
                batch.update(notificationRef, { read: true });
            });
            await batch.commit();

            // Update local state
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }, [notifications, user?.uid]);

    // Clear/delete a notification
    const clearNotification = useCallback(async (notificationId) => {
        if (!db) return;

        try {
            await fetch(`http://localhost:3001/api/notifications/${notificationId}`, {
                method: 'DELETE'
            });

            // Update local state
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            const wasUnread = notifications.find(n => n.id === notificationId)?.read === false;
            if (wasUnread) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    }, [notifications]);

    // Get notifications by type
    const getNotificationsByType = useCallback((type) => {
        return notifications.filter(n => n.type === type);
    }, [notifications]);

    const value = {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        clearNotification,
        getNotificationsByType
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}
